// 템플릿 양식 CSV — 양식 생성·헤더 연결·템플릿 자동 판별·중복 헤더·실행 템플릿 선택(순수 함수만 둔다)
import { CSV_BOM, CSV_LINE_BREAK, formatCsvRow } from 'utils/importExport/utils/csv'

// 첨부(files)는 CSV 셀로 표현할 수 없어 양식·판별 대상에서 뺀다(spec D3·D5)
export const getTemplateFormFields = (schema) =>
  schema.fields.filter((field) => field.type !== 'files')

export const buildTemplateCsv = (schema) =>
  CSV_BOM +
  formatCsvRow(getTemplateFormFields(schema).map((field) => field.labelLocalized)) +
  CSV_LINE_BREAK

export const normalizeHeader = (header) =>
  String(header ?? '')
    .normalize('NFC')
    .trim()
    .toLowerCase()

// 필드 별칭 = 영문 label + 템플릿이 가진 모든 로케일의 번역. 같은 Password 라도 템플릿마다 번역이 달라
// (Web Login '암호', Authenticator '비밀번호') 템플릿별로 만든다(spec D4)
export const getFieldAliases = (schema, field) =>
  new Set(
    [
      field.label,
      ...Object.values(schema.translations || {}).map((labelMap) => labelMap?.[field.label]),
    ]
      .filter(Boolean)
      .map(normalizeHeader),
  )

// 필드 순서대로 각 필드에 헤더 순서상 처음 맞는 아직 안 쓰인 헤더를 연결한다. 한 헤더는 한 필드에만 연결되고,
// 같은 필드에 맞는 두 번째 헤더는 연결되지 않아 미연결 열 경고에 나온다(spec D4)
export const buildHeaderColumnMap = (schema, headers) => {
  const normalizedHeaders = headers.map(normalizeHeader)
  const usedIndexes = new Set()
  const columnMap = {}
  getTemplateFormFields(schema).forEach((field) => {
    const aliases = getFieldAliases(schema, field)
    const index = normalizedHeaders.findIndex(
      (header, headerIndex) => !usedIndexes.has(headerIndex) && aliases.has(header),
    )
    if (index === -1) return
    usedIndexes.add(index)
    columnMap[field.label] = headers[index]
  })
  return columnMap
}

// 정규화한 이름이 겹치는 헤더를 원본 이름 그대로 돌려준다. 이름 없는 빈 헤더는 겹침으로 보지 않는다 —
// 끝에 빈 열이 여러 개 붙은 파일을 막지 않기 위해서다(미연결 열 경고도 빈 헤더를 뺀다 — spec D14)
export const findDuplicateHeaders = (headers) => {
  const countByHeader = new Map()
  headers.forEach((header) => {
    const key = normalizeHeader(header)
    if (key) countByHeader.set(key, (countByHeader.get(key) || 0) + 1)
  })
  return headers.filter((header) => countByHeader.get(normalizeHeader(header)) > 1)
}

// 끝에 붙은, 모든 칸이 빈 줄을 뗀다 — 그대로 두면 줄마다 '필수값 미입력' 오류가 쌓여 하나씩 제외해야 한다
// (importValidator.js:78-82 가 제목이 빈 레코드를 오류로 만든다). 중간의 빈 줄은 뒤 행의 번호가 바뀌지 않도록 둔다
export const dropTrailingEmptyRows = (rows) => {
  let end = rows.length
  while (
    end > 0 &&
    Object.values(rows[end - 1]).every((value) => String(value ?? '').trim() === '')
  ) {
    end -= 1
  }
  return rows.slice(0, end)
}

// catalogItems: [{ id, schema }] — 필수 필드를 모두 연결하는 템플릿 중 연결이 가장 많은 하나의 id.
// 후보가 없거나 1위가 동률이면 null(호출측이 기본 형식으로 내린다 — spec D5·D6)
export const matchTemplateByHeaders = (catalogItems, headers) => {
  if (findDuplicateHeaders(headers).length > 0) return null // '다음' 시점에 차단되는 입력(spec D15)
  const ranked = catalogItems
    .map(({ id, schema }) => {
      const fields = getTemplateFormFields(schema)
      const linked = buildHeaderColumnMap(schema, headers)
      const hasAllRequired = fields
        .filter((field) => field.required)
        .every((field) => linked[field.label])
      const linkedCount = Object.keys(linked).length
      return { id, hasAllRequired, linkedCount, missingCount: fields.length - linkedCount }
    })
    .filter((candidate) => candidate.hasAllRequired)
    // ① 연결 많은 순 ② 빠진 필드 적은 순 — Default 양식이 Login·SSH Key 와 연결 수가 같아도 Default 가 이긴다(T5e)
    .sort((a, b) => b.linkedCount - a.linkedCount || a.missingCount - b.missingCount)
  if (ranked.length === 0) return null
  const [best, second] = ranked
  const isTie =
    second && second.linkedCount === best.linkedCount && second.missingCount === best.missingCount
  return isTie ? null : best.id
}

// 실행은 선택한 템플릿이 아니라 매핑을 만든 템플릿으로 한다 — 매핑 뒤 업로드 화면에서 다른 파일을 올리면
// 선택이 바뀌기 때문이다(spec D9). 분기가 틀리면 전부 Default 로 조용히 들어가 테스트로 고정한다(T15)
export const resolveImportTemplateId = ({ presetId, mappingTemplateId }) =>
  presetId === 'template' ? mappingTemplateId : null
